import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject, take, takeUntil } from 'rxjs';

// Importaciones de tus librerías
import { AuthService, RefreshTokenService, IdleTimerService } from '@pulse-front/core';
import { AlertComponent } from '@pulse-front/ui/src/lib/alert/alert.component'; // Apuntamos directo si el index no lo exporta, o '@pulse-front/ui'

@Component({
  selector: 'panel-b2b-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  // Proveemos el IdleTimerService aquí para que se cree una instancia única para este layout
  providers: [IdleTimerService]
})
export class MainLayoutComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private refreshTokenService: RefreshTokenService,
    private idleService: IdleTimerService, // Inyectamos el servicio de inactividad
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit(): void {
    // 1. Escuchar Timeout Total (Sesión expirada por inactividad prolongada)
    this.idleService.onTimeout$
      .pipe(take(1)) // Solo necesitamos que ocurra una vez
      .subscribe(() => {
        this.sessionExpiredModal();
      });

    // 2. Escuchar aviso de Refresh (La sesión está por expirar)
    this.idleService.refreshToken$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshTokenModal();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- ACCIONES ---

  onLogout() {
    this.confirmSignOut();
  }

  private performLogout() {
    this.authService.logout(); // Usamos tu AuthService centralizado
  }

  // --- MODALES ---

  confirmSignOut() {
    const dialogRef = this.dialog.open(AlertComponent, {
      width: '420px',
      data: {
        header: 'Cerrar Sesión',
        body: '¿Estás seguro que deseas salir?',
        yesLabel: 'Salir',
        noLabel: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.authService.logout();
        this.router.navigate(['/auth/login']);
      }
    });
  }

  sessionExpiredModal() {
    const dialogRef = this.dialog.open(AlertComponent, {
      width: '400px',
      hasBackdrop: true,
      disableClose: true, // Obligatorio cerrar sesión
      data: {
        title: 'Sesión Expirada',
        body: 'Tu sesión ha terminado por inactividad.',
        type: 'error'
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.performLogout();
    });
  }

  refreshTokenModal() {
    // Usamos el mismo AlertComponent pero con lógica de decisión
    const dialogRef = this.dialog.open(AlertComponent, {
      width: '450px',
      hasBackdrop: true,
      disableClose: true,
      data: {
        title: 'Extender Sesión',
        body: 'Tu sesión está a punto de expirar. ¿Quieres mantenerte conectado?',
        type: 'info',
        // Propiedades hipotéticas para botones personalizados en tu Alert
        yesLabel: 'Sí, continuar',
        noLabel: 'No, salir'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      // Adaptación de la respuesta según tu ejemplo de pulse
      if (res === true || res?.event === 'yes-option' || res === 'yes') {
        this.refreshToken();
      } else {
        this.performLogout();
      }
    });
  }

  // --- LÓGICA DE NEGOCIO ---

  refreshToken() {
    this.refreshTokenService.refresh().subscribe({
      next: () => {
        console.log('Sesión extendida correctamente');
        // Aquí podrías reiniciar el timer si el servicio no lo hace automático
        // this.idleService.reset(); 
      },
      error: (err) => {
        console.error('Error al extender sesión', err);
        this.performLogout();
      }
    });
  }
}